/**
 * Theme tokens (IMPL-19 / architecture §16). Palette modeled on the "Nora" workspace reference:
 * a near-white light theme over a soft light-gray sidebar, and a near-black (OLED) dark theme, both
 * carried by a single vivid blue accent used for selection, links, primary actions, and status
 * pills. The grayscale ramp still holds on a reflective display (the Daylight): grayscale mode drops
 * the accent's hue to a neutral so nothing depends on color.
 */
export interface ThemeTokens {
  /** grayscale ramp, light -> dark */
  gray: Record<0 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
  /** the single attention accent (and its grayscale fallback) */
  accent: string;
  accentGray: string;
  bg: string;
  fg: string;
  muted: string;
  border: string;
}

export const lightTheme: ThemeTokens = {
  gray: {
    0: '#ffffff',
    50: '#f7f7f6',
    100: '#eeeeec',
    200: '#dededa',
    300: '#c4c4be',
    400: '#9a9a93',
    500: '#74746d',
    600: '#54544e',
    700: '#3a3a36',
    800: '#232320',
    900: '#121210',
  },
  accent: '#2563eb', // vivid blue — selection, links, primary actions, status pills
  accentGray: '#3a3a36',
  bg: '#ffffff',
  fg: '#1a1a19',
  muted: '#6f6f69', // 4.68:1 on #f6f6f5 — was #8a8a85 (3.47:1, AA fail) (A11Y-002)
  border: '#ececeb',
};

export const darkTheme: ThemeTokens = {
  gray: lightTheme.gray,
  // A11Y-005 (accepted): white-on-#3b82f6 primary buttons are 3.68:1 (below AA 4.5:1 for normal text).
  // Left as-is deliberately: `accent` is also used AS TEXT on dark (pills, sd-tags, links), where
  // darkening toward #2f6fe0 would *lower* contrast. A correct fix is a separate --lapo-accent-strong
  // button-surface token (tracked, not a quick win); until then the button contrast is an accepted gap.
  accent: '#3b82f6', // a touch brighter so it reads on the near-black surface
  accentGray: '#c4c4be',
  bg: '#1c1c1b',
  fg: '#ededec',
  muted: '#8d8d86',
  border: '#2e2e2c',
};

export type ThemeName = 'light' | 'dark';

/**
 * Full UI surface variables per theme. These mirror the `--lapo-*` custom properties that
 * `styles.css` declares on `:root` (side-bg, hover, active, faint, link, bullet …), not just the
 * four token colors on {@link ThemeTokens}. Switching themes has to override ALL of them or dark
 * mode would inherit light surfaces; keeping the full set here is what makes the toggle complete.
 */
interface UiSurface {
  bg: string;
  sideBg: string;
  fg: string;
  muted: string;
  faint: string;
  border: string;
  hover: string;
  active: string;
  link: string;
  bullet: string;
}

const LIGHT_SURFACE: UiSurface = {
  bg: '#ffffff',
  sideBg: '#f6f6f5',
  fg: '#1a1a19',
  muted: '#6f6f69', // 4.68:1 on #f6f6f5 — was #8a8a85 (3.47:1, AA fail) (A11Y-002)
  faint: '#b9b9b3',
  border: '#ececeb',
  hover: '#efefee',
  active: '#e6e6e4',
  link: '#2563eb',
  bullet: '#cacac4',
};

const DARK_SURFACE: UiSurface = {
  bg: '#1c1c1b',
  sideBg: '#161615',
  fg: '#ededec',
  muted: '#8d8d86',
  faint: '#585853',
  border: '#2e2e2c',
  hover: '#262624',
  active: '#323230',
  link: '#6ea8f5',
  bullet: '#56564f',
};

/** Build the CSS custom properties for a theme. `grayscale` drops the accent's hue. */
export function buildCssVars(name: ThemeName, opts: { grayscale?: boolean } = {}): Record<string, string> {
  const t = name === 'dark' ? darkTheme : lightTheme;
  const s = name === 'dark' ? DARK_SURFACE : LIGHT_SURFACE;
  const accent = opts.grayscale ? t.accentGray : t.accent;
  const vars: Record<string, string> = {
    '--lapo-bg': s.bg,
    '--lapo-side-bg': s.sideBg,
    '--lapo-fg': s.fg,
    '--lapo-muted': s.muted,
    '--lapo-faint': s.faint,
    '--lapo-border': s.border,
    '--lapo-hover': s.hover,
    '--lapo-active': s.active,
    '--lapo-link': opts.grayscale ? s.fg : s.link,
    '--lapo-bullet': s.bullet,
    '--lapo-accent': accent,
    // Editor selection/handles track the accent so they read against either surface (and go
    // neutral in grayscale mode along with everything else).
    '--lapo-select': accent,
  };
  for (const [k, v] of Object.entries(t.gray)) vars[`--lapo-gray-${k}`] = v;
  return vars;
}

/** Render the vars as an inline style string for the root element. */
export function cssVarString(name: ThemeName, opts: { grayscale?: boolean } = {}): string {
  return Object.entries(buildCssVars(name, opts))
    .map(([k, v]) => `${k}: ${v}`)
    .join('; ');
}
